import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import utcPlugin from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
import { failedSteps, scoreLabel } from "./taskHubUtils";
import { getInitials } from "../../requests/utilities/helpers";

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

// The two dialogs a submitted audit opens: build a follow-up task from its
// failed items, or schedule the same audit again at this store. Both pick a
// deadline on the STORE's clock (the form builder's rule): 5 PM chosen for
// a Pacific store means 5 PM there, whatever zone the manager sits in.

const boxStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 520,
  maxWidth: "92vw",
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  border: "2px solid #E2E8F0",
  borderRadius: "15px",
  boxShadow: 24,
  p: 4,
  outline: "none",
};

const inputSx = {
  sx: {
    backgroundColor: "white",
    borderRadius: "8px",
    fontSize: "14px",
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
  },
};

const pickerSlotProps = {
  day: { sx: { "&.MuiPickersDay-root.Mui-selected": { backgroundColor: "#6d28d9" } } },
  digitalClockSectionItem: {
    sx: {
      "&.Mui-selected": { backgroundColor: "#6d28d9", color: "white" },
      "&.Mui-selected:hover, &.Mui-selected:focus": { backgroundColor: "#5b21b6" },
    },
  },
  actionBar: { sx: { "& .MuiButton-root": { color: "#6d28d9" } } },
};

const pickerSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: "40px",
    backgroundColor: "white",
    borderRadius: "8px",
    fontSize: "14px",
    "&.Mui-focused fieldset": { borderColor: "#6d28d9" },
  },
};

export const zonedToIso = (value, tz) => {
  if (!value || !dayjs(value).isValid()) return null;
  if (!tz) return dayjs(value).toISOString();
  return dayjs.tz(dayjs(value).format("YYYY-MM-DDTHH:mm:ss"), tz).toISOString();
};

// Tomorrow at 5 PM on the store's clock: a sensible default deadline.
const defaultDue = (tz, daysAhead) => {
  const base = tz ? dayjs().tz(tz) : dayjs();
  return base.add(daysAhead, "day").hour(17).minute(0).second(0);
};

function DuePicker({ value, onChange, tz, label }) {
  return (
    <div className="mt-4">
      <p className="text-sm text-slate-500 mb-1">{label}</p>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateTimePicker value={value} onChange={onChange} slotProps={pickerSlotProps} sx={pickerSx} />
      </LocalizationProvider>
    </div>
  );
}

function Footer({ onCancel, onConfirm, confirmLabel, disabled }) {
  return (
    <div className="flex justify-end gap-2 mt-5">
      <button
        className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-medium"
        onClick={onCancel}
      >
        Cancel
      </button>
      <button
        className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${
          disabled ? "bg-slate-300 cursor-not-allowed" : "bg-violet-700 hover:bg-violet-800"
        }`}
        disabled={disabled}
        onClick={onConfirm}
      >
        {confirmLabel}
      </button>
    </div>
  );
}

// "Store managers" is the company_manager tier, the same reach the backend applies.
const MANAGER_TYPES = ["company_manager"];
const memberName = (m) =>
  (m?.full_name || `${m?.first_name || ""} ${m?.last_name || ""}`).trim() || "Team member";

// Pick which failed items become checkboxes on one follow-up task at this
// store, who it is for (the store's managers by default: the punch list is
// the GM's), optionally who exactly, name it, and give it a deadline.
export function FollowUpTaskModal({ open, occ, task, tz, members, onClose, onConfirm, busy }) {
  const fails = React.useMemo(() => failedSteps(occ), [occ]);
  const [audience, setAudience] = React.useState("company_manager");
  const [assignees, setAssignees] = React.useState([]);
  // The people the chosen audience reaches at this store (what the backend
  // will accept as a pin).
  const candidates = React.useMemo(
    () =>
      (members || [])
        .filter((m) => !m.is_archived && !m.is_hidden && m.permission_type !== "company_timeclock")
        .filter((m) => audience === "everyone" || MANAGER_TYPES.includes(m.permission_type))
        .sort((a, b) => memberName(a).localeCompare(memberName(b))),
    [members, audience]
  );
  // The item as it read when audited (a rename since does not apply).
  const stampedName = (s) =>
    (occ?.audit_items || []).find((i) => i?.uuid === s.plan_subtask?.uuid)?.name || s.plan_subtask?.name;
  const [selected, setSelected] = React.useState([]);
  const [name, setName] = React.useState("");
  const [due, setDue] = React.useState(null);
  React.useEffect(() => {
    if (!open) return;
    setSelected(fails.map((s) => s.uuid));
    setName(task?.name ? `${task.name} follow-ups` : "Audit follow-ups");
    setDue(defaultDue(tz, 3));
    setAudience("company_manager");
    setAssignees([]);
  }, [open, fails, task?.name, tz]);
  // A narrower audience drops assignees it no longer reaches.
  React.useEffect(() => {
    setAssignees((prev) => prev.filter((id) => candidates.some((m) => m.id === id)));
  }, [candidates]);
  const toggleAssignee = (id) =>
    setAssignees((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggle = (uuid) =>
    setSelected((prev) => (prev.includes(uuid) ? prev.filter((u) => u !== uuid) : [...prev, uuid]));
  const canConfirm = selected.length > 0 && !!due && !busy;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={boxStyle}>
        <h3 className="text-xl font-semibold text-slate-900">Create a follow-up task</h3>
        <p className="text-sm text-slate-500 mt-2">
          One task at this location with a checkbox per item you pick. Each carries the standard and the auditor's note.
        </p>
        <div className="mt-4">
          <p className="text-sm text-slate-500 mb-1">Task name</p>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            InputProps={inputSx}
          />
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm text-slate-500">
              Failed items · {selected.length} of {fails.length} selected
            </p>
            <button
              className="text-xs font-semibold text-violet-700 hover:text-violet-800"
              onClick={() =>
                setSelected(selected.length === fails.length ? [] : fails.map((s) => s.uuid))
              }
            >
              {selected.length === fails.length ? "Clear" : "Select all"}
            </button>
          </div>
          <div className="border border-slate-200 rounded-xl bg-white max-h-64 overflow-y-auto">
            {fails.map((s) => {
              const on = selected.includes(s.uuid);
              return (
                <label
                  key={s.uuid}
                  className="flex items-start gap-3 px-3 py-2 border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    className="mt-1 accent-violet-700"
                    checked={on}
                    onChange={() => toggle(s.uuid)}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-800">{stampedName(s)}</span>

                    {s.value_text && (
                      <span className="block text-xs text-slate-500 mt-0.5">{s.value_text}</span>
                    )}
                  </span>
                </label>
              );
            })}
            {fails.length === 0 && (
              <p className="px-3 py-4 text-sm text-slate-400">Nothing failed on this audit.</p>
            )}
          </div>
        </div>
        <div className="mt-4">
          <p className="text-sm text-slate-500 mb-1">Who does it?</p>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
            {[
              ["company_manager", "Store managers"],
              ["everyone", "Anyone at this location"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setAudience(key)}
                className={`px-3 py-1.5 text-xs font-semibold ${
                  audience === key ? "bg-violet-700 text-white" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm text-slate-500">
              Assign to <span className="text-slate-400">(optional)</span>
            </p>
            {assignees.length > 0 && (
              <button className="text-xs font-semibold text-violet-700 hover:text-violet-800" onClick={() => setAssignees([])}>
                Clear
              </button>
            )}
          </div>
          <div className="border border-slate-200 rounded-xl bg-white max-h-44 overflow-y-auto">
            {candidates.map((m) => {
              const on = assignees.includes(m.id);
              return (
                <label
                  key={m.id}
                  className="flex items-center gap-3 px-3 py-2 border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-slate-50"
                >
                  <input type="checkbox" className="accent-violet-700" checked={on} onChange={() => toggleAssignee(m.id)} />
                  <span className="bg-violet-100 text-violet-700 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold flex-none">
                    {getInitials(memberName(m))}
                  </span>
                  <span className="block text-sm font-medium text-slate-800 truncate">{memberName(m)}</span>
                </label>
              );
            })}
            {candidates.length === 0 && (
              <p className="px-3 py-3 text-sm text-slate-400">Nobody at this location matches.</p>
            )}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            {assignees.length
              ? "Only the people picked see it; the store's managers can still reassign it."
              : audience === "company_manager"
              ? "Every manager at this location sees it until someone picks it up."
              : "Everyone at this location sees it until someone picks it up."}
          </p>
        </div>
        <DuePicker value={due} onChange={setDue} tz={tz} label="Due" />
        <Footer
          onCancel={onClose}
          confirmLabel={busy ? "Creating..." : `Create task${selected.length ? ` (${selected.length})` : ""}`}
          disabled={!canConfirm}
          onConfirm={() =>
            onConfirm({ name, due_at: zonedToIso(due, tz), step_uuids: selected, audience, assignee_ids: assignees })
          }
        />

      </Box>
    </Modal>
  );
}

// The same checklist again at this store, due on a chosen date.
export function ReauditModal({ open, occ, task, tz, onClose, onConfirm, busy }) {
  const [due, setDue] = React.useState(null);
  React.useEffect(() => {
    if (open) setDue(defaultDue(tz, 14));
  }, [open, tz]);
  const label = scoreLabel(occ);
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={boxStyle}>
        <h3 className="text-xl font-semibold text-slate-900">Schedule a re-audit</h3>
        <p className="text-sm text-slate-500 mt-2">
          A copy of “{task?.name}” at this location, with the same items and scoring.
          {label ? ` This audit scored ${label}${occ?.score_grade ? ` (${occ.score_grade})` : ""}.` : ""}
        </p>
        <DuePicker value={due} onChange={setDue} tz={tz} label="Due" />
        <Footer
          onCancel={onClose}
          confirmLabel={busy ? "Scheduling..." : "Schedule re-audit"}
          disabled={!due || busy}
          onConfirm={() => onConfirm({ due_at: zonedToIso(due, tz) })}
        />
      </Box>
    </Modal>
  );
}
